import {constant} from '@packages/utilities/fp/utils';
import {TTypeApplication, TGeneric, TRowCons} from '@packages/systems/wfdl-ast';
import {matchType} from '../matchType';
import {
  type Type,
  getSystem,
  withSystem,
  getMeta,
  withMeta,
} from '../DataTypeDefinition';

// Editors read system and meta from checked types, so rebuilt nodes retain them.
export const retainAnnotations = (from: Type, next: Type): Type => {
  const system = getSystem(from);
  const typeWithSystem = system ? withSystem(system, next) : next;
  const meta = getMeta(from);
  return meta ? withMeta(meta, typeWithSystem) : typeWithSystem;
};

const replaceTypeRefsWithType = matchType<
  (
    arg1: Type,
    arg2: {
      [key: string]: Type;
    }
  ) => Type
>({
  TypeReference:
    (ref) =>
    (
      ty: Type,
      refs: {
        [key: string]: Type;
      }
    ) => {
      if (refs[ref]) {
        return retainAnnotations(ty, refs[ref]);
      }
      return ty;
    },
  TypeApplication:
    ({con, arg}) =>
    (
      ty: Type,
      refs: {
        [key: string]: Type;
      }
    ) => {
      const nextCon = replaceTypeRefs(refs, con);
      const nextArg = replaceTypeRefs(refs, arg);

      if (con === nextCon && arg === nextArg) return ty;
      return retainAnnotations(
        ty,
        TTypeApplication({con: nextCon, arg: nextArg})
      );
    },
  Generic:
    ({arg, body}) =>
    (
      ty: Type,
      refs: {
        [key: string]: Type;
      }
    ) => {
      if (refs[arg]) return ty;
      const nextBody = replaceTypeRefs(refs, body);
      if (nextBody === body) return ty;
      return retainAnnotations(ty, TGeneric({arg, body: nextBody}));
    },
  RowCons:
    ({label, type, tail}) =>
    (
      ty: Type,
      refs: {
        [key: string]: Type;
      }
    ) => {
      const nextType = replaceTypeRefs(refs, type);
      const nextTail = replaceTypeRefs(refs, tail);

      if (type === nextType && tail === nextTail) return ty;
      return retainAnnotations(
        ty,
        TRowCons({label, type: nextType, tail: nextTail})
      );
    },
  _: constant,
});

/**
 * Replaces all type references with their corresponding `replacement` type in RefSet, taking
 * shadowing into account.
 */
export const replaceTypeRefs = (
  refs: {
    [key: string]: Type;
  },
  ty: Type
): Type => {
  return replaceTypeRefsWithType(ty)(ty, refs);
};
